// /src/components/Navbar.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./navbar.css";

/* ================= fetch (no Redux) ================ */
function useCoursesForNavbarFetchOnly() {
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/courses", { credentials: "same-origin" });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];
        if (!cancelled) setCourses(list);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);
  return courses;
}

/* ================= date parsing ================= */
const MONTHS = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,sept:8,oct:9,nov:10,dec:11 };
function parseFirstDateFromString(s=""){
  const L = String(s).trim(); const d0 = new Date(L); if(!isNaN(d0)) return d0;
  const l = L.toLowerCase();
  let m = l.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/); if(m) return new Date(+m[1],+m[2]-1,+m[3]);
  m = l.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/); if(m){ const yr=m[3].length===2?+("20"+m[3]):+m[3]; return new Date(yr,+m[1]-1,+m[2]); }
  m = l.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\s+(\d{1,2})(?:\s*[–-]\s*\d{1,2})?(?:,)?\s+(\d{4})/);
  if(m) return new Date(+m[3], MONTHS[m[1]], +m[2]);
  m = l.match(/(\d{1,2})(?:\s*[–-]\s*\d{1,2})?\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\s+(\d{4})/);
  if(m) return new Date(+m[3], MONTHS[m[2]], +m[1]);
  return null;
}
function earliestUpcomingForCourse(c, todayMidnight){
  const raw = [c.startDate,c.start_date,c.startISO,c.startAt,c.start,c.dateISO,c.date,c.dates,c.schedule,c?.start?.date]
    .filter(Boolean).flatMap(v=>Array.isArray(v)?v:[v]);
  const cand = raw.map(String).map(parseFirstDateFromString).filter(d=>d&&!isNaN(d)).sort((a,b)=>+a-+b);
  for(const d of cand) if(d>=todayMidnight) return d; return null;
}

/* ================= top ticker ================= */
function NextClassTicker(){
  const courses = useCoursesForNavbarFetchOnly();

  const memo = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);

    // Find earliest upcoming date
    let best = null;
    (courses || []).forEach(c => {
      if (!c || c.sold === true) return;
      const d = earliestUpcomingForCourse(c, today);
      if (d && (!best || d < best)) best = d;
    });

    if (!best) {
      return {
        nextDate: null,
        dateLabel: "",
        deadlineLabel: "",
        showDeadline: false,
      };
    }

    const dateLabel = best.toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric"
    });

    // Registration deadline = 7 days before start
    const regDeadline = new Date(best);
    regDeadline.setDate(regDeadline.getDate() - 7);
    regDeadline.setHours(0,0,0,0);

    const deadlineLabel = regDeadline.toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric"
    });

    const showDeadline = regDeadline >= today;

    return { nextDate: best, dateLabel, deadlineLabel, showDeadline };
  }, [courses]);

  if (!memo.nextDate) return null;

  const Phrase = () => (
    <span className="ticker-item">
      <span className="dim">Next classes begin &nbsp;</span>
      <strong className="ink-grad">{memo.dateLabel}</strong>

      {memo.showDeadline && (
        <>
          <span className="sep" aria-hidden="true" />
          <span className="dim">Registration ends &nbsp;</span>
          <strong className="deadline">{memo.deadlineLabel}</strong>
        </>
      )}

      <span className="sep" aria-hidden="true" />
      <span className="dim">Bundle &amp; Save</span>
      <span className="sep" aria-hidden="true" />
      <Link to="/design" className="ticker-cta">Design Your Class Bundle</Link>
    </span>
  );

  return (
    <div className="ticker-shell" role="region" aria-label="Enrollment update">
      <div className="ticker-mask">
        <div className="ticker-track"><Phrase/><Phrase/><Phrase/><Phrase/></div>
        <div className="ticker-track ticker-track--dup"><Phrase/><Phrase/><Phrase/><Phrase/></div>
      </div>
    </div>
  );
}


/* ================= data ================= */
const FUNDAMENTALS = [
  { slug:"nano",  title:"Full Nano Brows",  days:3, price:3400 },
  { slug:"nanocombo", title:"Nano Combo Brows", days:3, price:4000 },
  { slug:"ombre", title:"Ombré Brows", days:2, price:2900 },
  { slug:"lips",  title:"Lip Blush",   days:2, price:3200 },
];

const BUNDLES = [
  { key:"ombre+lip",      title:"Ombré + Lip Blush",    price:5600, was:2900+3200 },
  { key:"nano+ombre",     title:"Full Nano Brows + Ombré", price:5900, was:3400+2900 },
  { key:"nano+lip",       title:"Full Nano Brows + Lip Blush", price:6100, was:3400+3200 },
  { key:"nanocombo+lip", title:"Nano Combo Brows + Lip Blush", price:6700, was:4000+3200 },
  { key:"nano+ombre+lip", title:"Full Nano Brows + Ombré + Lip Blush", price:8500, was:3400+2900+3200 },
];

/* Advanced – 1:1 mentorship with choose-your-own date */
const ADVANCED = [
  {
    slug: "lash-enhancement",
    title: "Lash Enhancement Tattoo",
    days: 1,
    price: 1900,
    desc: "1-on-1 mentorship",
  },
];

/* ================= navbar ================= */
export default function Navbar(){
  const [menuOpen,setMenuOpen]=useState(false);
  const [scrolled,setScrolled]=useState(false);
  const [navTop,setNavTop]=useState(0);
  const location=useLocation();
  const panelRef=useRef(null);

  useLayoutEffect(() => {
    function updateTop() {
      // We trust the CSS token; don't read element height.
      const h = parseFloat(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--ticker-h")
          .trim()
          .replace("px","")
      ) || 36;

      setNavTop(h);
      document.documentElement.style.setProperty("--ticker-h", `${h}px`);
      if (h > 1) document.documentElement.classList.add("has-ticker");
      else document.documentElement.classList.remove("has-ticker");
    }

    updateTop();
    // Re-apply on orientation change / resize (but still not measuring the DOM)
    window.addEventListener("resize", updateTop);
    window.addEventListener("orientationchange", updateTop);
    const t = setTimeout(updateTop, 200);

    return () => {
      window.removeEventListener("resize", updateTop);
      window.removeEventListener("orientationchange", updateTop);
      clearTimeout(t);
    };
  }, []);

  useEffect(()=>{
    const onScroll=()=>{ if(!menuOpen) setScrolled(window.pageYOffset>8); };
    onScroll(); window.addEventListener("scroll", onScroll, {passive:true});
    return ()=>window.removeEventListener("scroll", onScroll);
  },[menuOpen]);

  useEffect(()=>{ setMenuOpen(false); },[location.pathname]);

  const prevInline=useRef({}); const scrollYRef=useRef(0);
  useEffect(()=>{
    const body=document.body; const docEl=document.documentElement; if(!body||!docEl) return;
    if(menuOpen){
      prevInline.current={ overflow:body.style.overflow, position:body.style.position, top:body.style.top, width:body.style.width, paddingRight:body.style.paddingRight };
      const sbw=window.innerWidth-docEl.clientWidth; scrollYRef.current=window.scrollY;
      body.dataset.menuOpen="true"; body.style.overflow="hidden"; body.style.position="fixed";
      body.style.top=`-${scrollYRef.current}px`; body.style.width="100%"; if(sbw>0) body.style.paddingRight=`${sbw}px`;
    }else{
      const y=Math.abs(parseInt(body.style.top||"0",10))||0;
      body.dataset.menuOpen="false"; Object.assign(body.style, prevInline.current); window.scrollTo(0,y);
    }
  },[menuOpen]);

  useEffect(()=>{
    const onKey=e=>{ if(e.key==="Escape"&&menuOpen) setMenuOpen(false); };
    window.addEventListener("keydown", onKey); return ()=>window.removeEventListener("keydown", onKey);
  },[menuOpen]);

  const handleOverlayPointer=(e)=>{ if(panelRef.current && !panelRef.current.contains(e.target)) setMenuOpen(false); };

  const shellClass = useMemo(()=>["nav-shell", (menuOpen||scrolled)?"nav-shell--glassy":"nav-shell--transparent"].join(" "),[menuOpen,scrolled]);
  const brandClasses = ["brand-name","brand-readable", menuOpen?"brand--compact":""].join(" ");
  const fmtUSD = (n)=>`$${Number(n).toLocaleString(undefined,{maximumFractionDigits:0})}`;

  return (
    <>
      <NextClassTicker/>

      <header className={shellClass} aria-label="Primary" style={{position:"fixed", top:navTop, left:0, right:0, zIndex:8000}}>
        <Link to="/" className="brand-lines nav-brand-link" aria-label="COSMETINK ACADEMY Home">
          <div className={brandClasses}>
            <span className="brand-word-pre">COSMET</span>
            <span className="brand-ink-gradient"><span className="brand-ink-inner">INK</span></span>
            <span className="brand-word-post">ACADEMY</span>
          </div>
        </Link>

        <div className="nav-right-cluster">
          <Link to="/design" className="nav-btn design-btn gradient-glow" aria-label="Design & book your fundamental course">
            <span className="label-wide">Book Your Fundamental Course</span>
            <span className="label-narrow">Book Course</span>
          </Link>

          <button
            type="button"
            className={`nav-btn nav-menu-trigger ${menuOpen ? "nav-menu-trigger--open nav-menu-trigger--float" : ""}`}
            onClick={()=>setMenuOpen(v=>!v)} aria-expanded={menuOpen} aria-controls="nav-overlay"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            <div className="menu-trigger-inner">
              <div className="menu-trigger-bg"/>
              <div className="burger-wrap" aria-hidden>
                <span className="burger-line burger-line--top"/><span className="burger-line burger-line--mid"/><span className="burger-line burger-line--bot"/>
              </div>
              <span className="menu-word-label">Menu</span>
            </div>
          </button>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div className="menu-backdrop" onClick={()=>setMenuOpen(false)} initial={{opacity:0}} animate={{opacity:0.6}} exit={{opacity:0}}/>
            <motion.div
              id="nav-overlay" className="menu-overlay" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
              role="dialog" aria-modal="true" aria-label="Navigation Menu"
              onMouseDown={handleOverlayPointer} onTouchStart={handleOverlayPointer}
            >
              <motion.div
                ref={panelRef} className="menu-panel menu-panel--simple"
                initial={{y:18, opacity:0}} animate={{y:0, opacity:1}} exit={{y:18, opacity:0}}
                transition={{type:"tween", duration:0.2}}
                onMouseDown={(e)=>e.stopPropagation()} onTouchStart={(e)=>e.stopPropagation()}
              >
                {/* ============== FUNDAMENTALS ============== */}
                <section className="panel-block">
                  <h4 className="block-title">Fundamental Courses</h4>

                  <ul className="list-rows two-col no-bullets">
                    {FUNDAMENTALS.map((c) => (
                      <li key={c.slug} className="row row--tech">
                        <Link
                          to={`/courses/${c.slug}`}
                          onClick={() => setMenuOpen(false)}
                          className="row-link row-link--tech row-link--grid"
                        >
                          {/* Left: title + micro description */}
                          <div className="row-left">
                            <span className="row-title">{c.title}</span>
                            <span className="row-desc">
                              Full details, syllabus, kits & expectations.
                            </span>
                          </div>

                          {/* Top-right: days */}
                          <span className="row-days chip chip-circle">{c.days} Days</span>

                          {/* Bottom-left: price */}
                          <span className="row-price price-soft price-small">{fmtUSD(c.price)}</span>

                          {/* Bottom-right: “View details & book →” */}
                          <span className="row-cta">View details &nbsp;•&nbsp; Book&nbsp;→</span>
                        </Link>
                      </li>
                    ))}
                  </ul>

                  {/* Bundles */}
                  <div className="bundle-simple">
                    <div className="bundle-head">
                      <h5 className="bundle-title">Popular Bundles</h5>
                      <p className="bundle-sub dim">Combine techniques to reduce tuition.</p>
                    </div>

                    <div className="bundle-table">
                      {BUNDLES.map((b)=>{
                        const save=b.was-b.price;
                        return (
                          <div key={b.key} className="b-row">
                            <div className="b-left">
                              <div className="b-title-wrap">
                                <span className="b-name">{b.title}</span>
                                <span className="b-nonbundled dim">Non-bundled price {fmtUSD(b.was)}</span>
                              </div>
                            </div>
                            <div className="b-right">
                              <span className="b-now price-soft price-small">{fmtUSD(b.price)}</span>
                              <span className="b-save">Save {fmtUSD(save)}</span>
                              <Link to="/design" className="b-cta" onClick={()=>setMenuOpen(false)}>Design Bundle</Link>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </section>

                {/* ============== ADVANCED ============== */}
                <section className="panel-block panel-block--tight">
                  <h4 className="block-title shine-gold">Advanced Courses</h4>

                  <ul className="list-rows two-col no-bullets">
                    {ADVANCED.map((a)=>(
                      <li key={a.slug} className="row row--tech">
                        <Link
                          to={`/courses/${a.slug}`}
                          onClick={()=>setMenuOpen(false)}
                          className="row-link row-link--tech row-link--grid"
                        >
                          {/* Left: title + micro description */}
                          <div className="row-left">
                            <span className="row-title">{a.title}</span>
                            <span className="row-desc">
                              {a.desc || "1-on-1 mentorship • specialized attention • pick your date"}
                            </span>
                          </div>

                          {/* Top-right: days (chip) */}
                          <span className="row-days chip chip-circle">{a.days} Day</span>

                          {/* Bottom-left: price (soft) */}
                          <span className="row-price price-soft price-small">{fmtUSD(a.price)}</span>

                          {/* Bottom-right: CTA */}
                          <span className="row-cta">View details &nbsp;•&nbsp; Schedule&nbsp;→</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>

                {/* ============== OTHER LINKS (no label) ============== */}
                <section className="panel-block panel-block--tight">
                  <ul className="academy-tiles no-bullets">
                    <li>
                      <Link to="/apprenticeship" onClick={()=>setMenuOpen(false)} className="academy-tile">
                        <span className="tile-title">Apprenticeship</span>
                        <span className="tile-sub">Pathway &amp; requirements</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/trainers" onClick={()=>setMenuOpen(false)} className="academy-tile">
                        <span className="tile-title">Meet the Instructors</span>
                        <span className="tile-sub">Background, Experience, &amp; Approach</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/history" onClick={()=>setMenuOpen(false)} className="academy-tile">
                        <span className="tile-title">Our Story</span>
                        <span className="tile-sub">Origins</span>
                      </Link>
                    </li>
                  </ul>
                </section>

                <div className="panel-footer panel-footer--simple">
                  <Link to="/design" className="footer-cta" onClick={()=>setMenuOpen(false)}>
                    Book Your Fundamental Course
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {menuOpen && (
        <button
          type="button"
          className="nav-menu-trigger nav-menu-trigger--open nav-menu-trigger--float"
          aria-label="Close menu"
          onClick={()=>setMenuOpen(false)}
        >
          <div className="menu-trigger-inner">
            <div className="menu-trigger-bg"/>
            <div className="burger-wrap" aria-hidden>
              <span className="burger-line burger-line--top"/>
              <span className="burger-line burger-line--mid"/>
              <span className="burger-line burger-line--bot"/>
            </div>
            <span className="menu-word-label">Close</span>
          </div>
        </button>
      )}
    </>
  );
}
